import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import axiosInstance from 'axiosConfig';
import { queryClient } from 'App';
import { Toast } from 'components';
import { TOAST_TYPES_ENUM } from 'enums';
import { useSearchParams } from "react-router-dom";
import {
  getOfflineQueue,
  removeOfflineOperation,
  enqueueOfflineOperation,
} from 'hooks/useOfflineQueue';
import { deleteOfflineFileBlob } from 'hooks/useOfflineFileBlobs';

interface DeleteFileData {
  uuid: string; 
  type: string;
}

const removeOrderFilePlaceholderFromOfflineApiCache = (
  orderUuid: string,
  fileUuid: string
) => {
  try {
    const raw = localStorage.getItem('offlineApiCache');
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, any>;

    const keysToTry = [
      `orders/${orderUuid}`,
      `orders/${orderUuid}/`,
      `orders/${orderUuid}?showPhotos=true`,
    ];

    let touched = false;
    for (const key of keysToTry) {
      const entry = parsed[key];
      if (!entry) continue;

      const data = entry?.data ?? entry;
      const files = data?.files;
      if (!Array.isArray(files)) continue;

      const nextFiles = files.filter((f: any) => f?.uuid !== fileUuid);
      if (nextFiles.length === files.length) continue;

      if (entry?.data) {
        entry.data = { ...data, files: nextFiles };
      } else {
        parsed[key] = { ...data, files: nextFiles };
      }
      touched = true;
    }

    if (touched) {
      localStorage.setItem('offlineApiCache', JSON.stringify(parsed));
      window.dispatchEvent(new Event('offlineApiCacheChanged'));
    }
  } catch {
    // no-op
  }
};

const removeOptimisticUploadOperationForFile = (
  fileUuid: string
): { removed: boolean } => {
  try {
    const ops = getOfflineQueue().filter(
      (op) => (op.status === 'pending' || op.status === 'failed') && op.entityType === 'file'
    );

    // Nasz offline upload /files ma tempId = uuid placeholdera.
    const op = ops.find((x) => x.tempId === fileUuid);
    if (!op) return { removed: false };

    removeOfflineOperation(op.id);
    return { removed: true };
  } catch {
    return { removed: false };
  }
};

const useDeleteFile = (
  options?: UseMutationOptions<any, Error, DeleteFileData>
) => {
  const [searchParams] = useSearchParams();
  const orderUuid = searchParams.get("orderUuid");
  return useMutation({
    ...options,
    mutationKey: ['deleteFile'],
    mutationFn: async ({ uuid, type }: DeleteFileData) => {
      // ONLINE: usuwamy od razu w BE, bez kolejki.
      if (navigator.onLine) {
        return axiosInstance.delete(`/files/${uuid}`).then((res) => res.data);
      }

      // OFFLINE: dwa przypadki:
      // 1) zdjęcie dodane offline (uuid === tempId operacji uploadu) -> nie kolejkujemy DELETE,
      //    tylko usuwamy upload z kolejki + blob z IndexedDB.
      const removedUpload = removeOptimisticUploadOperationForFile(uuid);
      if (removedUpload.removed) {
        await deleteOfflineFileBlob(uuid).catch(() => undefined);
        if (orderUuid) {
          removeOrderFilePlaceholderFromOfflineApiCache(orderUuid, uuid);
        }
        return { status: 'offline-removed-local', uuid, type } as any;
      }

      // 2) zdjęcie z backendu (uuid serwerowe) -> kolejkujemy DELETE /files/{uuid}
      enqueueOfflineOperation({
        method: 'DELETE',
        url: `/files/${uuid}`,
        body: null,
        entityType: 'file',
        tempId: uuid,
      });

      if (orderUuid) {
        removeOrderFilePlaceholderFromOfflineApiCache(orderUuid, uuid);
      }

      return { status: 'queued-offline', uuid, type } as any;
    },
    onMutate: (variables) => {
      Toast({
        message: 'Trwa usuwanie pliku.',
        type: TOAST_TYPES_ENUM.LOADING,
        id: 'delete-file',
      });
      if (options && options.onMutate) {
        return options.onMutate(variables);
      }
    },
    onSuccess: (data, variables, context) => {
      // W OFFLINE nie invalidujemy (mogłoby nadpisać local placeholdery), a i tak robimy optimistic remove w UI.
      if (navigator.onLine) {
        queryClient.invalidateQueries({ queryKey: ['orderDetails', orderUuid] });
      }
      Toast({
        message: 'Plik został usunięty',
        type: TOAST_TYPES_ENUM.SUCCESS,
        id: 'delete-file',
      });
      if (options && options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      Toast({
        message: 'Plik nie został usunięty',
        type: TOAST_TYPES_ENUM.ERROR,
        id: 'delete-file',
      });
      if (options && options.onError) {
        options.onError(error, variables, context);
      }
    },
  });
};

export default useDeleteFile;
